import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Select } from "../components/ui";
import { CheckCircle2 } from "lucide-react";

const SKILL_LEVELS = [
  { value: "good", label: "Good" },
  { value: "average", label: "Average" },
  { value: "ok", label: "OK" },
];

const UMPIRE_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

export default function PlayerSkillsPage() {
  const nav = useNavigate();
  const { user, refreshMe } = useAuth();
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState({
    batting: "ok",
    bowling: "ok",
    fielding: "ok",
    keeping: "ok",
    can_umpire: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Update user profile with skills - using PATCH /auth/me endpoint
      const response = await api.patch("/auth/me", {
        batting_skill: skills.batting,
        bowling_skill: skills.bowling,
        fielding_skill: skills.fielding,
        keeping_skill: skills.keeping,
        can_umpire: skills.can_umpire,
        skills_set: true,
      });
      
      // Ensure we have the updated data
      await refreshMe();
      
      toast.success("Player skills saved!");
      
      // Small delay to ensure state update
      setTimeout(() => {
        nav("/home", { replace: true });
      }, 500);
    } catch (err) {
      toast.error(apiError(err));
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    // Skip to home
    nav("/home", { replace: true });
  };

  return (
    <div className="app-shell flex flex-col pb-10">
      <div className="px-6 pt-14 pb-4">
        <div className="h-14 w-14 rounded-3xl bg-brand text-white grid place-items-center mb-4">
          <CheckCircle2 size={26} strokeWidth={2} />
        </div>
        <h1 className="text-3xl font-bold">Your cricket skills</h1>
        <p className="text-ink-muted mt-1 text-[15px]">
          Tell us about your strengths so we can match you better in teams.
        </p>
      </div>

      <div className="px-5">
        <Card>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-ink mb-2">
                Batting
              </label>
              <Select
                value={skills.batting}
                onChange={(e) => setSkills({ ...skills, batting: e.target.value })}
                data-testid="batting-select"
              >
                {SKILL_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink mb-2">
                Bowling
              </label>
              <Select
                value={skills.bowling}
                onChange={(e) => setSkills({ ...skills, bowling: e.target.value })}
                data-testid="bowling-select"
              >
                {SKILL_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink mb-2">
                Fielding
              </label>
              <Select
                value={skills.fielding}
                onChange={(e) => setSkills({ ...skills, fielding: e.target.value })}
                data-testid="fielding-select"
              >
                {SKILL_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink mb-2">
                Keeping
              </label>
              <Select
                value={skills.keeping}
                onChange={(e) => setSkills({ ...skills, keeping: e.target.value })}
                data-testid="keeping-select"
              >
                {SKILL_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink mb-2">
                Can you umpire?
              </label>
              <Select
                value={skills.can_umpire.toString()}
                onChange={(e) => setSkills({ ...skills, can_umpire: e.target.value === "true" })}
                data-testid="umpire-select"
              >
                {UMPIRE_OPTIONS.map((option) => (
                  <option key={option.value.toString()} value={option.value.toString()}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleSkip}
                disabled={loading}
                data-testid="skills-skip-btn"
              >
                Skip
              </Button>
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                data-testid="skills-save-btn"
              >
                {loading ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
